const { getElement, getElements } = window.BAO.Utils
class BaoBundleUpsell extends window.BAO.CustomElement() {
  #shopifySectionId = 'shopify-section-bao-app-bundle-upsell'
  #abortController = null

  static get requiredElements() {
    return [
      'checkbox',
      'details',
      'radio',
      'options',
      'quantity',
      'input',
    ]
  }

  setupListeners() {
    super.setupListeners()

    this.handleRadioChange = this.handleRadioChange.bind(this)
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
    this.handleDetailsToggle = this.handleDetailsToggle.bind(this)

    this.addRadioListeners()
    this.addDetailsListeners()
  }

  addRadioListeners() {
    this.els?.radio?.elements?.forEach(radioEl => {
      this.listeners.add(radioEl, 'change', this.handleRadioChange)
    })
  }

  removeRadioListeners() {
    this.els?.radio?.elements?.forEach(radioEl => {
      this.listeners.remove(radioEl, 'change', this.handleRadioChange)
    })
  }

  addDetailsListeners() {
    if (!this.els?.checkbox?.element || !this.els?.details?.element) return

    this.listeners.add(this.els.checkbox.element, 'change', this.handleCheckboxChange)
    this.listeners.add(this.els.details.element, 'toggle', this.handleDetailsToggle)
  }

  /**
   * Handles the radio change event
   *
   * @param {Event} event
   */
  async handleRadioChange(event) {
    const checkedRadioEls = this.els?.radio?.elements?.filter(el => el.checked) ?? []
    const optionValues = checkedRadioEls.map(el => el.dataset.valueId)

    await this.renderUpsell(optionValues)
    this.setQuantity()

    if (event.isTrusted) getElement(`#${event.target.id}`, { context: document })?.focus()
  }

  /**
   * Handles the checkbox change event
   *
   * @param {Event} event
   */
  handleCheckboxChange(event) {
    const detailsEl = this.els?.details?.element
    if (!detailsEl) return

    detailsEl.open = event.target.checked
    this.setQuantity()
  }

  /**
   * Handles the details toggle event
   *
   * @param {Event} event
   */
  handleDetailsToggle(event) {
    const checkboxEl = this.els?.checkbox?.element
    if (!checkboxEl) return

    checkboxEl.checked = event.target.open
    checkboxEl.dispatchEvent(new Event('change'))

    this.updateAddToCartButtonText()
  }

  /**
   * Renders the upsell
   *
   * @param {string[]} optionValues
   */
  async renderUpsell(optionValues = []) {
    const controller = new AbortController()
    const { upsellUrl, sectionId } = this.dataset
    if (!this.els.options.element || !upsellUrl || !sectionId) return

    this.setIsLoading(true)

    this.#abortController?.abort()
    this.#abortController = controller

    const sectionUrl = `${upsellUrl}?section_id=${sectionId}&option_values=${optionValues.join(',')}`

    try {
      const htmlString = await window.BAO.sectionRenderer.fetchSingle(sectionId, sectionUrl, { signal: controller.signal })
      const html = window.BAO.sectionRenderer.getOuterHtml(htmlString, {
        id: this.#shopifySectionId,
        selector: this.els.options.selector,
      })

      this.els.options.element.outerHTML = html
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error(error)
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null
        this.setIsLoading(false)
      }
    }
  }

  setQuantity() {
    const checkedRadioEls = this.els?.radio?.elements?.filter(el => el.checked) ?? []
    const unavailableRadioEls = checkedRadioEls?.filter(el => el.dataset.available === 'false') ?? []

    const checkboxEl = this.els?.checkbox?.element
    const quantityEl = this.els?.quantity?.element
    if (!checkboxEl || !quantityEl) return

    quantityEl.value = (unavailableRadioEls.length || !checkboxEl.checked) ? 0 : 1
  }

  /**
   * Sets the loading state
   *
   * @param {boolean} isLoading
   */
  setIsLoading(isLoading) {
    if (!isLoading && this.#abortController !== null) return

    this.setAttribute('aria-busy', isLoading)

    if (isLoading) {
      this.removeRadioListeners()
    } else {
      this.setInputValue()
      this.addRadioListeners()
    }
  }

  setInputValue() {
    const inputEl = this.els?.input?.element
    if (!inputEl) return

    inputEl.value = this.els?.options?.element?.dataset?.variantId ?? inputEl.value
  }

  updateAddToCartButtonText() {
    const bundleComponentsEl = this.getBundleComponentsEl()
    if (!bundleComponentsEl) return

    const checkboxEls = getElements(this.els?.checkbox?.selector, { context: document })
    if (!checkboxEls.length) return

    const hasUpsell = !!checkboxEls.find(el => el.checked)

    bundleComponentsEl.setAttribute('data-has-upsell', hasUpsell)
    bundleComponentsEl.toggleAddToCartButtonText()
  }

  getBundleComponentsEl() {
    return getElement('bao-app-bundle-components', { context: document })
  }
}

if (!window.customElements.get('bao-app-bundle-upsell')) {
  window.customElements.define('bao-app-bundle-upsell', BaoBundleUpsell)
}
